/**
 * PlanProposalCard — rendered inside a master thread's message timeline when
 * the agent emits a multi-worker plan for user approval (M4.5).
 *
 * Matches the wireframe in the plan spec:
 *   - Header: "Proposed plan (N workers, N tasks)"
 *   - Numbered item list: slug, title, "Creates task: ... under <anchor>"
 *   - Action row: [Approve & run] [Edit plan] [Discard]
 */

import { Button } from "../ui/button";
import type { CmmdAnchorKind } from "@cmmd-forge/contracts";
import {
  buildPlanProposalSummary,
  buildAnchorLabel,
  type PlanProposalItem,
} from "./PlanProposalCard.logic";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PlanProposalCardProps {
  readonly items: readonly PlanProposalItem[];
  readonly anchorKind: CmmdAnchorKind;
  readonly anchorCmmdId: string | null;
  readonly state?: "pending_approval" | "materializing";
  readonly onApprove: () => void;
  readonly onEdit: () => void;
  readonly onDiscard: () => void;
  readonly disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PlanProposalCard({
  items,
  anchorKind,
  anchorCmmdId,
  state = "pending_approval",
  onApprove,
  onEdit,
  onDiscard,
  disabled = false,
}: PlanProposalCardProps) {
  const summary = buildPlanProposalSummary(items);
  const anchorLabel = buildAnchorLabel(anchorKind, anchorCmmdId);
  const materializing = state === "materializing";

  return (
    <div
      className="rounded-lg border bg-background/80 p-4 shadow-sm"
      data-testid="plan-proposal-card"
    >
      {/* Header */}
      <div className="mb-3 text-sm font-medium tracking-[0.12em] text-muted-foreground uppercase">
        Proposed plan ({summary})
      </div>

      {/* Item list */}
      <ol className="mb-4 space-y-2">
        {items.map((item, index) => (
          <li key={item.slug} className="flex flex-col gap-0.5">
            <div className="flex items-start gap-2 text-sm">
              <span className="shrink-0 tabular-nums text-muted-foreground/60">{index + 1}.</span>
              <div className="min-w-0 flex-1">
                <span className="font-mono text-xs text-muted-foreground">{item.slug}</span>
                <span className="ml-2 text-foreground">{item.title}</span>
                {item.summary ? (
                  <p className="mt-0.5 text-xs text-muted-foreground/60">{item.summary}</p>
                ) : null}
                <p className="mt-0.5 text-[10px] text-muted-foreground/40 tracking-[0.12em]">
                  Creates task: &ldquo;{item.title}&rdquo; under {anchorLabel}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ol>

      {/* Action row */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="default"
          data-testid="plan-proposal-approve"
          disabled={disabled || materializing || items.length === 0}
          onClick={() => onApprove()}
        >
          {materializing ? "Creating tasks…" : "Approve & run"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="plan-proposal-edit"
          disabled={true}
          onClick={onEdit}
          title="Edit plan is not yet available"
        >
          Edit plan
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          data-testid="plan-proposal-discard"
          disabled={disabled || materializing}
          onClick={onDiscard}
        >
          Discard
        </Button>
      </div>
    </div>
  );
}
